import { ChangeDetectionStrategy, Component, computed, inject, viewChild, ViewEncapsulation } from '@angular/core';
import { MatButtonModule } from '@angular/material/button';
import { MatButtonToggleModule } from '@angular/material/button-toggle';
import { MatDialog } from '@angular/material/dialog';
import { MatIcon } from '@angular/material/icon';
import { MatListModule } from '@angular/material/list';
import { MatMenuModule } from '@angular/material/menu';
import { MatTooltipModule } from '@angular/material/tooltip';
import { Store } from '@ngrx/store';
import { AppService } from '@src/suite/app.service';
import { DateRangePagerComponent } from '@src/suite/common/components/date-range-pager/date-range-pager';
import { PagerComponent } from '@src/suite/common/components/pager/pager';
import { NomWebServicesJson } from '@src/suite/common/services/nom-web-services-json';
import { VisualStateService } from '@src/suite/common/services/visual-state-service';
import { selectI18N } from '@src/suite/common/store/i18n/selectors/i18n.selectors';
import { ViewStateActions } from '@src/suite/common/store/navigation/actions/viewstate.actions';
import { selectCurrentGroup, selectCurrentSection } from '@src/suite/common/store/navigation/selectors/navigation.selectors';
import {
    selectCurrentLayoutValue,
    selectCurrentViewState,
    selectCurrentViewStateType,
    selectGroupByDateTimeField,
    selectIsMapLayout,
    selectIsTileLayout,
    selectViewStateCurrentMapCenter,
    selectViewStateCurrentZoom,
    selectViewStateMapLockMapLocation,
    selectViewStateMapShowTeamLocations,
    selectViewStateMapZoomToCoverage,
} from '@src/suite/common/store/navigation/selectors/viewstate.selectors';
import { PlaybookActions } from '@src/suite/common/store/playbook/playbook.actions';
import { selectTileViewDateRange, selectTotalGroupCount } from '@src/suite/common/store/playbook/playbook.selector';
import { PlaybookMapViewComponent } from '@src/suite/playbook/components/playbook-map-view/playbook-map-view';

@Component({
    selector: 'st-playbook-right-slot',
    imports: [DateRangePagerComponent, MatButtonModule, MatButtonToggleModule, MatIcon, MatListModule, MatMenuModule, MatTooltipModule, PagerComponent],
    templateUrl: './playbook-right-slot.component.html',
    styleUrl: './playbook-right-slot.component.scss',
    changeDetection: ChangeDetectionStrategy.OnPush,
    encapsulation: ViewEncapsulation.None,
    host: {
        class: 'st-playbook-right-slot',
    },
})
export class PlaybookRightSlotComponent {
    store = inject(Store);
    appService = inject(AppService);
    visualStateService = inject(VisualStateService);
    nomWebServicesJson = inject(NomWebServicesJson);
    dialog = inject(MatDialog);

    selectViewStateType = this.store.selectSignal(selectCurrentViewStateType);
    viewStateLayoutValue = this.store.selectSignal(selectCurrentLayoutValue);
    languageSettings = this.store.selectSignal(selectI18N)();

    viewStateMapZoomToCoverage = this.store.selectSignal(selectViewStateMapZoomToCoverage);
    viewStateMapShowTeamLocations = this.store.selectSignal(selectViewStateMapShowTeamLocations);
    viewStateMapLockMapLocation = this.store.selectSignal(selectViewStateMapLockMapLocation);
    viewStateCurrentZoom = this.store.selectSignal(selectViewStateCurrentZoom);
    viewStateCurrentMapCenter = this.store.selectSignal(selectViewStateCurrentMapCenter);

    isTileLayout = this.store.selectSignal(selectIsTileLayout);
    isMapLayout = this.store.selectSignal(selectIsMapLayout);
    isDateField = this.store.selectSignal(selectGroupByDateTimeField);
    tileViewDateRange = this.store.selectSignal(selectTileViewDateRange);
    totalGroupCount = this.store.selectSignal(selectTotalGroupCount);

    // TODO: fix playbook map view, as it is no longer a child of this component in the visual tree, the viewChild reference is breaking and causing errors when trying to call methods on it. Need to find a new way to communicate with the map view component.
    readonly playbookMapView = viewChild(PlaybookMapViewComponent);

    readonly viewState = computed(() => this.store.selectSignal(selectCurrentViewState)());
    readonly group = computed(() => this.store.selectSignal(selectCurrentGroup)());
    readonly section = computed(() => this.store.selectSignal(selectCurrentSection)());

    readonly viewStateLoaded = computed(() => {
        return !!this.viewState();
    });

    readonly viewStateLayout = computed(() => {
        if (this.visualStateService.isLessThanLarge()) return null;
        return this.viewStateLayoutValue();
    });

    public showTeamLocations() {
        this.store.dispatch(ViewStateActions.toggleViewStateShowTeamLocations());
    }

    public lockMapLocation() {
        this.store.dispatch(ViewStateActions.toggleViewStateLockMapLocation());
    }

    public zoomToCoverage() {
        this.store.dispatch(ViewStateActions.toggleViewStateZoomToCoverage());
    }

    public onStartIndexChange(startIndex: number) {
        this.store.dispatch(PlaybookActions.setStartIndex({ startIndex }));
    }
}
